import React, { useState } from "react";
import { FormControl, FormLabel, Input, FormHelperText, Box, Stack, Button, Typography } from "@mui/joy";
import { sanitizeUserVisibleMessage } from "../utils/errorMessages";

interface OTPVerificationProps {
  phoneNumber: string;
  onVerify: (code: string) => Promise<void>;
  onSendCode: () => Promise<void>;
  loading?: boolean;
  error?: string;
  otpSent?: boolean;
  timeRemaining?: number;
}

/**
 * Reusable OTP verification component
 * Eliminates duplication across email/phone/google flows
 */
export function OTPVerification({
  phoneNumber,
  onVerify,
  onSendCode,
  loading = false,
  error,
  otpSent = false,
  timeRemaining = 0,
}: OTPVerificationProps) {
  const [code, setCode] = useState<string>("");
  const [verifying, setVerifying] = useState<boolean>(false);
  const [localError, setLocalError] = useState<string>("");

  const handleSendCode = async () => {
    setLocalError("");
    try {
      await onSendCode();
    } catch (err: any) {
      setLocalError(sanitizeUserVisibleMessage(err?.message, "Failed to send code"));
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      setLocalError("Code must be 6 digits");
      return;
    }

    setVerifying(true);
    setLocalError("");
    try {
      await onVerify(code);
    } catch (err: any) {
      setLocalError(sanitizeUserVisibleMessage(err?.message, "Verification failed"));
    } finally {
      setVerifying(false);
    }
  };

  const displayError = error || localError;

  if (!otpSent) {
    return (
      <Box
        sx={{
          mt: 1,
          p: 2,
          borderRadius: "14px",
          background:
            "linear-gradient(180deg, rgba(18,24,34,0.8) 0%, rgba(12,16,24,0.92) 100%)",
          border: "1px solid",
          borderColor: "rgba(122,162,247,0.22)",
        }}
      >
        <Typography level="body-sm" sx={{ color: "neutral.300", mb: 1 }}>
          We&apos;ll send a 6-digit verification code to your phone.
        </Typography>
        <Button
          loading={loading}
          onClick={handleSendCode}
          fullWidth
          sx={{
            mt: 1,
            fontWeight: 700,
            py: 1.25,
            borderRadius: "12px",
            background:
              "linear-gradient(90deg, rgba(68,138,255,1) 0%, rgba(94,173,255,1) 100%)",
            color: "#0A0F19",
            "&:hover": {
              background:
                "linear-gradient(90deg, rgba(78,146,255,1) 0%, rgba(109,184,255,1) 100%)",
            },
          }}
        >
          Send Verification Code to {phoneNumber}
        </Button>
        {displayError && (
          <Typography level="body-sm" sx={{ color: "danger.500", mt: 1 }}>
            {displayError}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 1,
        p: 2,
        borderRadius: "14px",
        background:
          "linear-gradient(180deg, rgba(18,24,34,0.8) 0%, rgba(12,16,24,0.92) 100%)",
        border: "1px solid",
        borderColor: "rgba(122,162,247,0.22)",
      }}
    >
      <FormControl>
        <FormLabel>Verification Code</FormLabel>
        <Input
          placeholder="Enter 6-digit code"
          value={code}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
            setCode(digits);
          }}
          inputMode="numeric"
          disabled={verifying}
          sx={{
            mt: 0.5,
            borderRadius: "12px",
            "--Input-focusedThickness": "2px",
            "& input": {
              textAlign: "center",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "1.2rem",
              fontWeight: 700,
              letterSpacing: "0.24em",
            },
            "& input::placeholder": {
              letterSpacing: "0.08em",
              fontWeight: 500,
              fontSize: "1rem",
            },
          }}
          slotProps={{
            input: {
              maxLength: 6,
            },
          }}
        />
        <FormHelperText sx={{ color: "neutral.400", mt: 1 }}>
          Use the 6-digit OTP sent to your phone number.
        </FormHelperText>
        {displayError && (
          <FormHelperText sx={{ color: "danger.500" }}>
            {displayError}
          </FormHelperText>
        )}
      </FormControl>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          loading={verifying}
          onClick={handleVerify}
          disabled={code.length !== 6 || verifying}
          fullWidth
          sx={{
            py: 1.25,
            borderRadius: "12px",
            fontWeight: 700,
            background:
              "linear-gradient(90deg, rgba(68,138,255,1) 0%, rgba(94,173,255,1) 100%)",
            color: "#0A0F19",
            "&:hover": {
              background:
                "linear-gradient(90deg, rgba(78,146,255,1) 0%, rgba(109,184,255,1) 100%)",
            },
            "&:disabled": {
              opacity: 0.45,
              color: "rgba(255,255,255,0.55)",
            },
          }}
        >
          Verify Code
        </Button>
      </Stack>

      <Box sx={{ mt: 2, textAlign: "center" }}>
        {timeRemaining > 0 && (
          <FormHelperText sx={{ color: "neutral.300" }}>
            Resend code in {timeRemaining}s
          </FormHelperText>
        )}
        {timeRemaining === 0 && (
          <Button
            variant="plain"
            size="sm"
            onClick={handleSendCode}
            loading={loading}
            sx={{
              color: "primary.300",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "rgba(94,173,255,0.12)",
              },
            }}
          >
            Didn't receive code? Resend
          </Button>
        )}
      </Box>
    </Box>
  );
}
